import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import styles from './SyncWithCalendarContent.module.scss';
import { useTranslation } from 'react-i18next';
import { CustomButton } from 'engage/components/Button/Button';
import googleIcon from '../../assets/images/google.svg';
import outlookIcon from '../../assets/images/outlook.svg';
import appleIcon from '../../assets/images/apple.svg';
import ms365Icon from '../../assets/images/ms365.svg';
import { ToastNotification } from 'engage/components/Toast/Toast';
import VideoService from 'engage/service/VideoService';
import * as Sentry from '@sentry/browser';
import { useUserContext } from 'engage/context/UserContext';
import CloseButton from 'engage/components/CloseButton/CloseButton';

const CALENDARS = {
    GOOGLE: 0,
    OUTLOOK: 1,
    APPLE: 2,
    MS365: 3,
};

const SyncWithCalendarContent = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(null);

    const { t } = useTranslation();

    const isMacLike = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

    const { user, appConfig } = useUserContext();

    const [selectedCalendarId, setSelectedCalendarId] = useState(CALENDARS.GOOGLE);

    const calendarProviders = useMemo(() => [
        ...(isMacLike ? [{
            id: CALENDARS.APPLE,
            name: 'Apple Calendar',
            iconSrc: appleIcon,
            description: t('schedule.calendar.sync_apple'),
        }] : []),
        {
            id: CALENDARS.GOOGLE,
            name: 'Google Calendar',
            iconSrc: googleIcon,
            description: t('schedule.calendar.sync_google'),
        },
        {
            id: CALENDARS.OUTLOOK,
            name: 'Outlook.com',
            iconSrc: outlookIcon,
            description: t('schedule.calendar.sync_outlook'),
        },
        {
            id: CALENDARS.MS365,
            name: 'Microsoft 365',
            iconSrc: ms365Icon,
            description: t('schedule.calendar.sync_ms365'),
        }], [isMacLike, t]);

    const selectedCalendarData = useMemo(() => calendarProviders.find(item => item.id == selectedCalendarId), [calendarProviders, selectedCalendarId]);

    const handleSyncWithCalendar = useCallback(async () => {
        setLoading(selectedCalendarId);
        try {
            const usersCalendarIcs = await VideoService.getUserCalendarIcs();
            if (!usersCalendarIcs?.path) {
                console.error('No calendar data found');
                return;
            }
            const webcalPath = usersCalendarIcs.path.replace('https://', 'webcal://');
            let path = '';
            switch (selectedCalendarId) {
                case CALENDARS.APPLE:
                    path = webcalPath;
                    break;
                case CALENDARS.GOOGLE:
                    path = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(webcalPath)}`
                    break;
                default:
                    const msUrl = selectedCalendarId == CALENDARS.OUTLOOK ? 'live' : 'office';
                    // Outlook.com & Ms365
                    path = `https://outlook.${msUrl}.com/calendar/0/addfromweb/?url=${encodeURIComponent(webcalPath)}&name=${encodeURIComponent(`${appConfig.name} & Vormats`)}`;
            }
            window.open(path, '_blank');
        } catch (e) {
            Sentry.captureException(`Error while synchronizing user ${user.username} with ${selectedCalendarData?.name}: ${e}`);
            ToastNotification({ message: t('schedule.calendar.sync_error'), closeButton: true });
        } finally {
            onClose();
        }
    }, [selectedCalendarId]);

    return <div className={styles.syncModal}>
        <div className={styles.header}>
            <h1 className={styles.title}>{t('schedule.calendar.sync_title')}</h1>
            <CloseButton onClose={onClose} />
        </div>
        <ul className={styles.supportedCalendars}>
            {calendarProviders.map(item =>
                <li key={item.id} className={cx(styles.calendarItem, { [styles.disabled]: loading != null, [styles.active]: item.id == selectedCalendarId })} onClick={() => setSelectedCalendarId(item.id)}>
                    <img src={item.iconSrc} alt={item.name} className={cx(styles.calendarIcon, styles[item.id])} />
                </li>
            )}
        </ul>
        <p className={styles.description}><span className={styles.infoIcon}>i</span> {selectedCalendarData.description}</p>
        <div className={styles.footer}>
            <CustomButton variant='secondary' onClick={onClose} label={t('general.cancel')} disabled={loading} />
            <CustomButton loading={loading !== null} loadingSpinner={true} loadingText={null} variant='primary' onClick={handleSyncWithCalendar} label={t('schedule.calendar.start_sync')} />
        </div>
    </div>
}

export default SyncWithCalendarContent;